import { apiClient } from "./apiClient";

export interface DiagnosticsCounters {
  redis_active: number;
  userposition_open: number;
  tradelog_open: number;
  queue_blocked: number;
  mismatches: number;
  critical_blockers: number;
}

export interface DiagnosticsRow {
  symbol: string;
  account_id: string;
  position_id: string;
  redis: {
    exists: boolean;
    position_id: string;
    entry_price: number | null;
    quantity: number | null;
  };
  user_position: {
    exists: boolean;
    position_id: string;
    state: string;
    entry_price: number | null;
    exit_price: number | null;
    qty: number | null;
  };
  trade_log: {
    row_count: number;
    open_rows: Array<{
      id: string;
      status: string;
      position_id: string;
      reason: string;
    }>;
  };
  queue_gate: {
    blocked: boolean;
  };
  issues: Array<{ severity: string; label: string; description: string }>;
  severity: string;
  suggested_fixes: string[];
  detected_at: string;
}

export interface ExecutionDiagnosticsResponse {
  rows: DiagnosticsRow[];
  counters: DiagnosticsCounters;
  total: number;
  critical_count: number;
  warning_count: number;
  timestamp: string;
}

export interface DiagnosticsDetailResponse {
  symbol: string;
  account_id: string;
  diagnostics: Record<string, unknown>;
  redis_raw: Record<string, unknown> | null;
  user_position_raw: Record<string, unknown> | null;
  trade_log_raw: string | null;
  block_reason: string | null;
}

export interface DiagnosticsActionRequest {
  action: string;
  symbol: string;
  account_id: string;
  position_id: string;
  reason: string;
}

export interface DiagnosticsActionResponse {
  success: boolean;
  message: string;
  audit: Record<string, unknown>;
}

const BASE_URL = "/admin/exit-signals/execution-diagnostics";

export async function getDiagnosticsCounters(): Promise<DiagnosticsCounters> {
  try {
    const response = await apiClient.get<DiagnosticsCounters>(
      `${BASE_URL}/counters`
    );
    return response.data;
  } catch (error: any) {
    const message =
      error?.response?.data?.detail ||
      error?.message ||
      "Failed to fetch diagnostics counters";
    const err = new Error(message) as Error & { statusCode?: number };
    err.statusCode = error?.response?.status;
    throw err;
  }
}

export async function getExecutionDiagnostics(params: {
  symbol?: string;
  account_id?: string;
  mismatch_only?: boolean;
  blocked_only?: boolean;
}): Promise<ExecutionDiagnosticsResponse> {
  try {
    const response = await apiClient.get<ExecutionDiagnosticsResponse>(
      BASE_URL,
      { params }
    );
    return response.data;
  } catch (error: any) {
    const message =
      error?.response?.data?.detail ||
      error?.message ||
      "Failed to fetch execution diagnostics";
    const err = new Error(message) as Error & { statusCode?: number };
    err.statusCode = error?.response?.status;
    throw err;
  }
}

export async function getDiagnosticsDetail(
  symbol: string,
  accountId: string
): Promise<DiagnosticsDetailResponse> {
  try {
    const response = await apiClient.get<DiagnosticsDetailResponse>(
      `${BASE_URL}/detail`,
      { params: { symbol, account_id: accountId } }
    );
    return response.data;
  } catch (error: any) {
    const message =
      error?.response?.data?.detail ||
      error?.message ||
      "Failed to fetch diagnostics detail";
    const err = new Error(message) as Error & { statusCode?: number };
    err.statusCode = error?.response?.status;
    throw err;
  }
}

export async function executeDiagnosticsAction(
  request: DiagnosticsActionRequest
): Promise<DiagnosticsActionResponse> {
  try {
    const response = await apiClient.post<DiagnosticsActionResponse>(
      `${BASE_URL}/action`,
      request
    );
    return response.data;
  } catch (error: any) {
    const message =
      error?.response?.data?.detail ||
      error?.message ||
      "Failed to execute diagnostics action";
    const err = new Error(message) as Error & { statusCode?: number };
    err.statusCode = error?.response?.status;
    throw err;
  }
}
